// Procedural WebAudio sound effects + ambient music.
// No external assets — all generated via oscillators and filters.

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let musicNodes: OscillatorNode[] = [];
let musicTimer: number | null = null;
let muted = false;

function ensure(): AudioContext {
  if (!ctx) {
    ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.5;
    masterGain.connect(ctx.destination);
    musicGain = ctx.createGain();
    musicGain.gain.value = 0.12;
    musicGain.connect(masterGain);
  }
  return ctx!;
}

export function unlockAudio() {
  const c = ensure();
  if (c.state === 'suspended') c.resume();
}

export function setMuted(m: boolean) {
  muted = m;
  if (masterGain) masterGain.gain.value = m ? 0 : 0.5;
}
export function isMuted() { return muted; }

function tone(freq: number, dur: number, type: OscillatorType = 'sine', vol = 0.3, slideTo?: number) {
  if (muted) return;
  const c = ensure();
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, c.currentTime);
  if (slideTo !== undefined) o.frequency.exponentialRampToValueAtTime(slideTo, c.currentTime + dur);
  g.gain.setValueAtTime(vol, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
  o.connect(g); g.connect(masterGain!);
  o.start();
  o.stop(c.currentTime + dur);
}

function noise(dur: number, vol = 0.3, filterFreq = 2000) {
  if (muted) return;
  const c = ensure();
  const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const src = c.createBufferSource();
  src.buffer = buf;
  const filt = c.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.value = filterFreq;
  const g = c.createGain();
  g.gain.value = vol;
  src.connect(filt); filt.connect(g); g.connect(masterGain!);
  src.start();
}

// ---- public SFX ----
export const SFX = {
  hit:        () => { tone(220, 0.08, 'square', 0.18, 110); noise(0.05, 0.15, 1500); },
  crit:       () => { tone(880, 0.18, 'sawtooth', 0.32, 220); tone(440, 0.18, 'square', 0.18); noise(0.12, 0.2, 3000); },
  explosion:  () => { noise(0.45, 0.5, 800); tone(80, 0.3, 'sawtooth', 0.3, 30); },
  freeze:     () => { tone(1200, 0.5, 'sine', 0.2, 200); tone(800, 0.5, 'triangle', 0.15, 150); },
  heal:       () => { tone(523, 0.15, 'sine', 0.25); setTimeout(() => tone(659, 0.15, 'sine', 0.25), 80); setTimeout(() => tone(784, 0.2, 'sine', 0.25), 160); },
  lightning:  () => { noise(0.15, 0.4, 5000); tone(1500, 0.1, 'sawtooth', 0.2, 400); },
  fireball:   () => { noise(0.3, 0.35, 1200); tone(220, 0.25, 'sawtooth', 0.25, 80); },
  meteor:     () => { tone(60, 0.6, 'sawtooth', 0.4, 30); noise(0.6, 0.4, 600); },
  shield:     () => { tone(440, 0.2, 'sine', 0.25); tone(660, 0.2, 'sine', 0.2); },
  ultimate:   () => { tone(330, 0.4, 'sawtooth', 0.35, 880); tone(165, 0.4, 'square', 0.25, 440); noise(0.3, 0.3, 4000); },
  whoosh:     () => { noise(0.2, 0.25, 1800); },
  spellReady: () => { tone(880, 0.08, 'sine', 0.25); setTimeout(() => tone(1320, 0.1, 'sine', 0.25), 80); },
  killStreak: () => { tone(440, 0.1, 'square', 0.3); setTimeout(() => tone(660, 0.1, 'square', 0.3), 90); setTimeout(() => tone(880, 0.15, 'square', 0.3), 180); },
  victory:    () => { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => tone(f, 0.25, 'square', 0.3), i * 130)); },
  defeat:     () => { [523, 466, 415, 349].forEach((f, i) => setTimeout(() => tone(f, 0.3, 'sawtooth', 0.25), i * 150)); },
  fight:      () => { tone(220, 0.1, 'square', 0.3); setTimeout(() => tone(330, 0.15, 'square', 0.3), 100); setTimeout(() => tone(440, 0.25, 'square', 0.35), 220); },
};

// ---- ambient music: a slow looping arpeggio over a pad ----
export function startMusic() {
  if (musicTimer != null) return;
  const c = ensure();
  // Pad: two detuned saws through a lowpass.
  const pad = (freq: number) => {
    const o = c.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = freq;
    const o2 = c.createOscillator();
    o2.type = 'sawtooth';
    o2.frequency.value = freq * 1.005;
    const filt = c.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = 600;
    const g = c.createGain();
    g.gain.value = 0.5;
    o.connect(filt); o2.connect(filt); filt.connect(g); g.connect(musicGain!);
    o.start(); o2.start();
    musicNodes.push(o, o2);
  };
  pad(110); // A2
  pad(165); // E3

  // Arpeggio: A minor pentatonic-ish loop.
  const notes = [220, 261.63, 329.63, 440, 329.63, 261.63];
  let i = 0;
  const step = () => {
    const f = notes[i % notes.length];
    const o = c.createOscillator();
    o.type = 'triangle';
    o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0.001, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, c.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.5);
    o.connect(g); g.connect(musicGain!);
    o.start();
    o.stop(c.currentTime + 0.55);
    i++;
  };
  musicTimer = window.setInterval(step, 380);
}

export function stopMusic() {
  if (musicTimer != null) { clearInterval(musicTimer); musicTimer = null; }
  musicNodes.forEach(n => { try { n.stop(); } catch {} });
  musicNodes = [];
}
